import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, CircularProgress, Chip, Avatar, Button, Card, CardContent,
  TextField, MenuItem, Stack, Snackbar, Alert, Switch, FormControlLabel, Divider
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import PersonIcon from '@mui/icons-material/Person';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  AppUser,
  UserService,
  UserUpdatePayload,
  formatUserDate,
  fromDatetimeLocalValue,
  getUserDisplayName,
  getUserInitials,
  toDatetimeLocalValue,
} from '../services/UserService';
import PageHeader from '../components/ui/PageHeader';

const STATUS_OPTIONS = ['pending', 'active', 'blocked', 'banned'];
const SUBSCRIPTION_STATUS_OPTIONS = ['active', 'expired', 'cancelled', 'grace_period', 'paused'];
const SUBSCRIPTION_TYPE_OPTIONS = ['yearly', 'monthly', 'weekly'];

interface UserFormState {
  username: string;
  email: string;
  status: string;
  emailVerified: boolean;
  isPro: boolean;
  language: string;
  notifications: boolean;
  proActivatedAt: string;
  subscriptionStatus: string;
  subscriptionType: string;
  subscriptionDate: string;
  proExpiryDate: string;
  subscriptionDuration: string;
  isSandbox: boolean;
  lastTransactionId: string;
  originalTransactionId: string;
}

const buildFormState = (user: AppUser): UserFormState => ({
  username: user.username ?? '',
  email: user.email ?? '',
  status: user.status ?? 'pending',
  emailVerified: user.emailVerified ?? false,
  isPro: user.isPro ?? false,
  language: user.preferences?.language ?? 'tr',
  notifications: user.preferences?.notifications ?? true,
  proActivatedAt: toDatetimeLocalValue(user.proActivatedAt),
  subscriptionStatus: user.subscription?.subscriptionStatus ?? '',
  subscriptionType: user.subscription?.subscriptionType ?? '',
  subscriptionDate: toDatetimeLocalValue(user.subscription?.subscriptionDate),
  proExpiryDate: toDatetimeLocalValue(user.subscription?.proExpiryDate),
  subscriptionDuration: user.subscription?.subscriptionDuration?.toString() ?? '',
  isSandbox: user.subscription?.isSandbox ?? false,
  lastTransactionId: user.subscription?.lastTransactionId ?? '',
  originalTransactionId: user.subscription?.originalTransactionId ?? '',
});

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [user, setUser] = useState<AppUser | null>(null);
  const [form, setForm] = useState<UserFormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const loadUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    const data = await UserService.getUserById(userId);
    setUser(data);
    if (data) setForm(buildFormState(data));
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const updateField = <K extends keyof UserFormState>(key: K, value: UserFormState[K]) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const handleSave = async () => {
    if (!userId || !form) return;

    setSaving(true);

    const subscription = form.isPro
      ? {
          subscriptionStatus: form.subscriptionStatus || undefined,
          subscriptionType: form.subscriptionType || undefined,
          subscriptionDate: fromDatetimeLocalValue(form.subscriptionDate) ?? undefined,
          proExpiryDate: fromDatetimeLocalValue(form.proExpiryDate) ?? undefined,
          subscriptionDuration: form.subscriptionDuration ? Number(form.subscriptionDuration) : undefined,
          isSandbox: form.isSandbox,
          lastTransactionId: form.lastTransactionId || undefined,
          originalTransactionId: form.originalTransactionId || undefined,
        }
      : null;

    const payload: UserUpdatePayload = {
      username: form.username.trim(),
      email: form.email.trim(),
      status: form.status,
      emailVerified: form.emailVerified,
      isPro: form.isPro,
      proActivatedAt: form.isPro ? fromDatetimeLocalValue(form.proActivatedAt) : null,
      preferences: {
        language: form.language,
        notifications: form.notifications,
      },
      subscription,
    };

    const success = await UserService.updateUser(userId, payload);

    if (success) {
      setSnackbar({ open: true, message: 'Kullanıcı başarıyla güncellendi', severity: 'success' });
      await loadUser();
    } else {
      setSnackbar({ open: true, message: 'Güncelleme sırasında hata oluştu', severity: 'error' });
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!user || !form) {
    return (
      <Box sx={{ width: '100%' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
          Kullanıcılara Dön
        </Button>
        <Typography color="text.secondary">Kullanıcı bulunamadı.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')} sx={{ mb: 2 }}>
        Kullanıcılara Dön
      </Button>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        Firestore&apos;daki <strong>emailVerified</strong> alanı, Firebase Authentication ile otomatik senkron değildir.
        Auth&apos;ta doğrulanmış olsa bile burada false görünebilir — mobil uygulama güncellemediği için.
      </Alert>

      <PageHeader
        title={getUserDisplayName(user)}
        subtitle={`${user.email || 'E-posta yok'} · UID: ${user.id}`}
        icon={
          <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontSize: '1rem' }}>
            {getUserInitials(user)}
          </Avatar>
        }
        actions={
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
          </Button>
        }
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 320px' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Stack spacing={3}>
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
                  <PersonIcon color="primary" fontSize="small" />
                  <Typography variant="subtitle1" fontWeight={600}>Profil Bilgileri</Typography>
                </Stack>
                <Stack spacing={2}>
                  <TextField
                    label="Kullanıcı Adı"
                    value={form.username}
                    onChange={(e) => updateField('username', e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="E-posta"
                    value={form.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                    <TextField
                      select
                      label="Hesap Durumu"
                      value={form.status}
                      onChange={(e) => updateField('status', e.target.value)}
                      size="small"
                      sx={{ minWidth: 180, flex: 1 }}
                    >
                      {STATUS_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>{option}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      label="Dil"
                      value={form.language}
                      onChange={(e) => updateField('language', e.target.value)}
                      size="small"
                      sx={{ minWidth: 120, flex: 1 }}
                    >
                      <MenuItem value="tr">Türkçe</MenuItem>
                      <MenuItem value="en">English</MenuItem>
                    </TextField>
                  </Stack>
                  <Stack direction="row" spacing={3} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.emailVerified}
                          onChange={(e) => updateField('emailVerified', e.target.checked)}
                        />
                      }
                      label="E-posta doğrulandı"
                    />
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.notifications}
                          onChange={(e) => updateField('notifications', e.target.checked)}
                        />
                      }
                      label="Bildirimler açık"
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <WorkspacePremiumIcon color="primary" fontSize="small" />
                    <Typography variant="subtitle1" fontWeight={600}>Abonelik Bilgileri</Typography>
                  </Stack>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.isPro}
                        onChange={(e) => updateField('isPro', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={form.isPro ? 'Pro Üye' : 'Free Üye'}
                  />
                </Stack>

                {form.isPro ? (
                  <Stack spacing={2}>
                    <TextField
                      label="Pro Aktivasyon Tarihi"
                      type="datetime-local"
                      value={form.proActivatedAt}
                      onChange={(e) => updateField('proActivatedAt', e.target.value)}
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                    />
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <TextField
                        select
                        label="Abonelik Durumu"
                        value={form.subscriptionStatus}
                        onChange={(e) => updateField('subscriptionStatus', e.target.value)}
                        size="small"
                        sx={{ minWidth: 160, flex: 1 }}
                      >
                        {SUBSCRIPTION_STATUS_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Abonelik Tipi"
                        value={form.subscriptionType}
                        onChange={(e) => updateField('subscriptionType', e.target.value)}
                        size="small"
                        sx={{ minWidth: 140, flex: 1 }}
                      >
                        {SUBSCRIPTION_TYPE_OPTIONS.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <TextField
                        label="Abone Olma Tarihi"
                        type="datetime-local"
                        value={form.subscriptionDate}
                        onChange={(e) => updateField('subscriptionDate', e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 220 }}
                        InputLabelProps={{ shrink: true }}
                      />
                      <TextField
                        label="Abonelik Bitiş Tarihi"
                        type="datetime-local"
                        value={form.proExpiryDate}
                        onChange={(e) => updateField('proExpiryDate', e.target.value)}
                        size="small"
                        sx={{ flex: 1, minWidth: 220 }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                      <TextField
                        label="Süre (gün)"
                        value={form.subscriptionDuration}
                        onChange={(e) => updateField('subscriptionDuration', e.target.value.replace(/[^0-9]/g, ''))}
                        size="small"
                        sx={{ minWidth: 120 }}
                      />
                      <FormControlLabel
                        control={
                          <Switch
                            checked={form.isSandbox}
                            onChange={(e) => updateField('isSandbox', e.target.checked)}
                          />
                        }
                        label="Sandbox (test)"
                      />
                    </Stack>
                    <TextField
                      label="Son İşlem ID"
                      value={form.lastTransactionId}
                      onChange={(e) => updateField('lastTransactionId', e.target.value)}
                      size="small"
                      fullWidth
                    />
                    <TextField
                      label="Orijinal İşlem ID"
                      value={form.originalTransactionId}
                      onChange={(e) => updateField('originalTransactionId', e.target.value)}
                      size="small"
                      fullWidth
                    />
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Bu kullanıcı Free planda. Pro yapmak için yukarıdaki anahtarı açın ve abonelik bilgilerini doldurun.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Stack>

        <Card sx={{ position: { lg: 'sticky' }, top: { lg: 24 } }}>
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SettingsIcon color="action" fontSize="small" />
                <Typography variant="subtitle1" fontWeight={600}>Özet</Typography>
              </Stack>
              <Stack spacing={1.5} divider={<Divider flexItem />}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Abonelik</Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={form.isPro ? `Pro · ${form.subscriptionType || '—'}` : 'Free'}
                      color={form.isPro ? 'primary' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                <InfoRow label="Abone Olma" value={formatUserDate(fromDatetimeLocalValue(form.subscriptionDate) ?? undefined)} />
                <InfoRow label="Pro Aktivasyon" value={formatUserDate(fromDatetimeLocalValue(form.proActivatedAt) ?? undefined)} />
                <InfoRow label="Bitiş Tarihi" value={formatUserDate(fromDatetimeLocalValue(form.proExpiryDate) ?? undefined)} />
                <InfoRow label="Kayıt Tarihi" value={formatUserDate(user.createdAt)} />
                <InfoRow label="Son Giriş" value={formatUserDate(user.lastLoginAt)} />
                <InfoRow label="Son Güncelleme" value={formatUserDate(user.updatedAt)} />
                <Box>
                  <Typography variant="caption" color="text.secondary">Push Token</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all', mt: 0.5, fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {user.pushToken || '—'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={500}>{value}</Typography>
  </Box>
);

export default UserDetail;
